<template>
  <div class="posts-calendar">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <b-button size="sm" variant="outline-primary" @click="prevMonth">Prev</b-button>
      <div class="h5 m-0">{{ monthYearLabel }}</div>
      <b-button size="sm" variant="outline-primary" @click="nextMonth">Next</b-button>
    </div>

    <div class="calendar-grid">
      <div class="calendar-header" v-for="d in weekDayLabels" :key="d">{{ d }}</div>
      <div
        v-for="(cell, idx) in calendarCells"
        :key="idx"
        class="calendar-cell"
        :class="{ 'is-today': cell.isToday, 'is-other': !cell.inMonth, 'is-selected': isSameDate(cell.date, selectedDate) }"
        @click="selectDate(cell.date)"
      >
        <div class="date-label">{{ cell.date.getDate() }}</div>
        <div class="events">
          <div
            v-for="(p, i) in limitedPosts(cell.date)"
            :key="p.id || i"
            class="event-line"
            :class="statusClass(p)"
            :title="p.post"
            @click.stop="$emit('post-click', p)"
          >
            <span class="dot" :class="statusClass(p)"></span>
            <span class="text">{{ eventTitle(p) }}</span>
          </div>
          <div v-if="hiddenCount(cell.date) > 0" class="more" @click.stop="selectDate(cell.date)">
            {{ hiddenCount(cell.date) }} more
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PostsCalendar',
  props: {
    posts: { type: Array, default: () => [] }, // Ayrshare history array
    value: { type: Date, default: () => new Date() }, // selected date v-model
  },
  data() {
    return {
      viewDate: new Date(this.value),
      selectedDate: new Date(this.value),
    }
  },
  computed: {
    monthYearLabel() {
      return this.viewDate.toLocaleString(undefined, { month: 'long', year: 'numeric' })
    },
    weekDayLabels() {
      const base = []
      const date = new Date(2021, 7, 1) // arbitrary Sunday
      for (let i = 0; i < 7; i++) {
        base.push(date.toLocaleString(undefined, { weekday: 'short' }))
        date.setDate(date.getDate() + 1)
      }
      return base
    },
    calendarCells() {
      const start = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1)
      const end = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 0)
      const startDay = start.getDay()
      const daysInMonth = end.getDate()

      const cells = []
      // leading blanks
      for (let i = 0; i < startDay; i++) {
        const d = new Date(start)
        d.setDate(d.getDate() - (startDay - i))
        cells.push(this.buildCell(d, false))
      }
      // month days
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), d)
        cells.push(this.buildCell(date, true))
      }
      // trailing blanks to fill 6 rows
      while (cells.length % 7 !== 0 || cells.length < 42) {
        const last = cells[cells.length - 1].date
        const d = new Date(last)
        d.setDate(d.getDate() + 1)
        cells.push(this.buildCell(d, false))
      }
      return cells
    },
  },
  watch: {
    value(newVal) {
      this.selectedDate = new Date(newVal)
      this.viewDate = new Date(newVal)
    }
  },
  methods: {
    isSameDate(a, b) {
      return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
    },
    normalizeDateString(s) {
      try { return new Date(s) } catch { return null }
    },
    tallyForDate(date) {
      const dt = new Date(date)
      if (!(dt instanceof Date) || isNaN(dt.getTime())) {
        return { posted: 0, scheduled: 0 }
      }
      const y = dt.getFullYear(), m = dt.getMonth(), d = dt.getDate()
      let posted = 0, scheduled = 0
      ;(Array.isArray(this.posts) ? this.posts : []).forEach(p => {
        const when = p && (p.scheduleDate ? this.normalizeDateString(p.scheduleDate) : (p.created ? this.normalizeDateString(p.created) : null))
        if (!when) return
        if (when.getFullYear() === y && when.getMonth() === m && when.getDate() === d) {
          if (p.type === 'scheduled' || p.status === 'scheduled' || p.status === 'pending') scheduled++
          else posted++
        }
      })
      return { posted, scheduled }
    },
    buildCell(date, inMonth) {
      const today = new Date()
      const { posted, scheduled } = this.tallyForDate(date)
      return {
        date,
        inMonth,
        isToday: this.isSameDate(date, today),
        countPosted: posted,
        countScheduled: scheduled,
      }
    },
    prevMonth() {
      const d = new Date(this.viewDate)
      d.setMonth(d.getMonth() - 1)
      this.viewDate = d
    },
    nextMonth() {
      const d = new Date(this.viewDate)
      d.setMonth(d.getMonth() + 1)
      this.viewDate = d
    },
    selectDate(d) {
      this.selectedDate = new Date(d)
      this.$emit('input', this.selectedDate)
      this.$emit('change', this.selectedDate)
      const posts = this.postsForDate(this.selectedDate)
      this.$emit('day-click', { date: this.selectedDate, posts })
    },
    postsForDate(date) {
      const dt = new Date(date)
      if (!(dt instanceof Date) || isNaN(dt.getTime())) return []
      const y = dt.getFullYear(), m = dt.getMonth(), day = dt.getDate()
      const arr = Array.isArray(this.posts) ? this.posts : []
      return arr.filter(p => {
        const when = p && (p.scheduleDate ? this.normalizeDateString(p.scheduleDate) : (p.created ? this.normalizeDateString(p.created) : null))
        if (!when) return false
        return when.getFullYear() === y && when.getMonth() === m && when.getDate() === day
      })
    },
    limitedPosts(date) {
      return this.postsForDate(date).slice(0, 3)
    },
    hiddenCount(date) {
      const total = this.postsForDate(date).length
      return total > 3 ? total - 3 : 0
    },
    statusClass(p) {
      if (!p) return ''
      if (String(p.status).toLowerCase() === 'success') return 'posted'
      if (String(p.status).toLowerCase() === 'error' || String(p.status).toLowerCase() === 'failed') return 'error'
      if (String(p.status).toLowerCase() === 'scheduled' || String(p.status).toLowerCase() === 'pending' || p.type === 'scheduled') return 'scheduled'
      return ''
    },
    eventTitle(p) {
      if (p && typeof p.post === 'string' && p.post.length) return p.post.slice(0, 28)
      return 'Post'
    }
  }
}
</script>

<style scoped>
.posts-calendar { display: block; }
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 100px;
  grid-gap: 6px;
}
.calendar-header {
  text-align: center;
  font-weight: 600;
  padding: 6px 0;
}
.calendar-cell {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  position: relative;
}
.calendar-cell.is-other { opacity: 0.45; }
.calendar-cell.is-today { border-color: #ff6b35; box-shadow: 0 0 0 2px rgba(255,107,53,0.25); }
.calendar-cell.is-selected { outline: 2px solid #007bff; }
.date-label { font-weight: 600; }
.events { margin-top: 6px; display: flex; flex-direction: column; gap: 2px; }
.event-line { display: flex; align-items: center; gap: 6px; font-size: 11px; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer; }
.event-line.posted .dot { background: #4caf50; }
.event-line.scheduled .dot { background: #ff9800; }
.event-line .dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
.event-line.error .dot { background: #e53935; }
.event-line .text { overflow: hidden; text-overflow: ellipsis; }
.more { margin-top: 2px; font-size: 11px; color: #007bff; cursor: pointer; }
</style>

